import type { ReactNode } from 'react'

import { Chip } from '@gto/ui/chip'
import { Typography } from '@gto/ui/typography'

import { HighlightedText } from './highlighted-text'

/** Width the toaster is pinned to above `sm`, in px. */
export const TOASTER_CARD_WIDTH = 369

/**
 * How the media sits in the card — the rendering half of the backend's
 * `toaster_media_layout`.
 *
 * `inset` is what every stored toaster has: the asset keeps the frame's padding
 * and its own corner radius, and the chip sits above it. `full_bleed` runs the
 * asset to the card edges instead, so the artwork reads as a hero band rather
 * than as a picture inside a panel.
 */
export type ToasterMediaLayout = 'inset' | 'full_bleed'

/**
 * Height the hero band runs at, in px.
 *
 * Fixed rather than left to the asset's own aspect ratio because an operator
 * uploads whatever they have: a band of a known depth keeps the copy below it in
 * the same place whatever arrives, and `object-cover` absorbs the difference.
 *
 * Applied as an inline style rather than a height class, for the same reason
 * `SLIDE_MIN_HEIGHT` is: Tailwind 3's scale steps 20 to 24 with nothing at 22,
 * so the class form has to spell the pixels out — and the design-system ratchet
 * counts an arbitrary-pixel class as drift too. The class would trade one
 * tracked metric for another and net nothing. (Spelling that class out here
 * would itself count: the ratchet reads raw file text, comments included.)
 */
export const TOASTER_HERO_HEIGHT = 88

export type ToasterCardProps = {
  /**
   * Contents of the "New" chip. Passed in because the app translates it (and
   * appends a `(2/3)` counter when several toasters are stacked) while the admin
   * preview, which has no i18n runtime, passes the English label.
   */
  newLabel: ReactNode
  /** Plays autoplay/muted/loop above the title; wins over `imageSrc` when both are set. */
  videoSrc?: string
  imageSrc?: string
  title: string
  /** Substring of `title` to paint in the accent colour; empty string = no accent. */
  titleHighlight: string
  description: ReactNode
  /** Defaults to `inset`, which is how every toaster rendered before the hero layout existed. */
  mediaLayout?: ToasterMediaLayout
  /** Defaults to `true`, so a card that says nothing about the chip still draws it. */
  showNewLabel?: boolean
}

type MediaProps = {
  videoSrc: string | undefined
  imageSrc: string | undefined
  className: string
  style: { height: number } | undefined
}

/**
 * Video wins over an image when both are set, which is the rule every toaster
 * has followed. Pulled out of the card only because the two layouts hand it
 * different classes and it must not be written twice.
 */
const ToasterMedia = ({ videoSrc, imageSrc, className, style }: MediaProps) => {
  if (videoSrc !== undefined) {
    return (
      <video
        autoPlay
        loop
        muted
        playsInline
        className={className}
        src={videoSrc}
        style={style}
      />
    )
  }

  if (imageSrc !== undefined) {
    return (
      <img
        alt=""
        className={className}
        src={imageSrc}
        style={style}
      />
    )
  }

  return null
}

/**
 * The headline, with any accent phrase in the primary colour.
 *
 * Uses `HighlightedText` rather than `AnnouncementTitle`, whose non-accent half
 * is forced to `text-white`. A toaster's headline has always INHERITED its
 * colour, and every stored toaster carries no accent phrase at all — so taking
 * the forced-white version would repaint copy on announcements this change is
 * supposed to leave alone.
 */
type ToasterTitleProps = {
  title: string
  highlight: string
}

const ToasterTitle = ({ title, highlight }: ToasterTitleProps) => (
  <HighlightedText
    highlight={highlight}
    highlightClassName="text-primary-main"
    text={title}
  />
)

/**
 * The body of one toaster — chips, media, headline, copy — exactly as customers
 * see it.
 *
 * Deliberately excludes the `Paper`, the close button and the auto-close
 * progress bar: in the app those wrap the whole slider rather than an individual
 * toaster, so they belong to `Toaster` and to whatever frames this in a preview.
 *
 * The two layouts are written as separate trees rather than as one tree full of
 * conditional classes. They order their children differently — the chip sits
 * ABOVE the media when it is inset and below the band when it bleeds — and a
 * shared tree would have to express that with a second condition on top of the
 * class ones, which is where the inset layout would quietly drift.
 *
 * Each tree carries its OWN side and top padding, so a card is laid out
 * correctly wherever it renders. The frame around it pads nothing: in the app it
 * holds a slider over several toasters, and a frame that padded for the active
 * one would repad the card sliding out from under it.
 */
export const ToasterCard = ({
  newLabel,
  videoSrc,
  imageSrc,
  title,
  titleHighlight,
  description,
  mediaLayout = 'inset',
  showNewLabel = true,
}: ToasterCardProps) => {
  // `className` differs per layout, so the chip is built by the branch that
  // knows which spacing it needs rather than shared as one element.
  const chip = (className?: string) =>
    showNewLabel ? (
      <Chip
        className={className}
        color="primary"
        label={newLabel}
        size="medium"
        variant="outlined"
      />
    ) : null

  if (mediaLayout === 'full_bleed') {
    return (
      <div>
        <ToasterMedia
          className="block w-full object-cover"
          imageSrc={imageSrc}
          style={{ height: TOASTER_HERO_HEIGHT }}
          videoSrc={videoSrc}
        />
        {/* Nothing pads the band above, which is how it reaches the card edges,
            so the copy under it has to bring its own inset. */}
        <div className="flex flex-col gap-3 px-4 pt-4">
          {showNewLabel && <div className="flex items-center gap-2">{chip()}</div>}
          <Typography variant="h6">
            <ToasterTitle
              highlight={titleHighlight}
              title={title}
            />
          </Typography>
          <Typography variant="body2">{description}</Typography>
        </div>
      </div>
    )
  }

  return (
    <div className="px-4 pt-4">
      {showNewLabel && <div className="mb-2 flex items-center gap-2">{chip('mb-4')}</div>}
      <ToasterMedia
        className="mb-2 w-full rounded-sm"
        imageSrc={imageSrc}
        style={undefined}
        videoSrc={videoSrc}
      />
      <Typography
        className="mb-2"
        variant="h6"
      >
        <ToasterTitle
          highlight={titleHighlight}
          title={title}
        />
      </Typography>
      <Typography variant="body2">{description}</Typography>
    </div>
  )
}
